import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { PRODUCTS, CATEGORIES, COMING_SOON_CATEGORIES } from "../data/products";
import { useTestRide } from "../components/TestRideContext";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("cat") || "all";
  const [active, setActive] = useState(initial);
  const { openModal } = useTestRide();

  const isComingSoon = COMING_SOON_CATEGORIES.includes(active);

  const filtered = useMemo(() => {
    if (active === "all") return PRODUCTS;
    return PRODUCTS.filter((p) => (p.categories || [p.category]).includes(active));
  }, [active]);

  const setCat = (id) => {
    setActive(id);
    if (id === "all") setParams({});
    else setParams({ cat: id });
  };

  return (
    <div data-testid="products-page" className="bg-white">
      <section className="relative pt-16 pb-20 border-b border-slate-200 overflow-hidden bg-gradient-to-b from-[#F0F6FF] to-white">
        <div className="absolute inset-0 helix-bg" />
        <div className="container-x relative">
          <p className="eyebrow">The line-up</p>
          <h1 className="font-outfit text-5xl sm:text-6xl lg:text-7xl font-bold mt-5 leading-[0.98] tracking-tighter max-w-4xl text-slate-900">
            Choose the right eBikeGo <br />
            <span className="text-slate-400">for your ride.</span>
          </h1>
          <p className="mt-6 text-slate-600 text-lg max-w-2xl">
            A focused lineup of electric bicycles for commuting, earning, utility and everyday city life. Every model is manufacturer-backed and service-ready at every eBikeGo store.
          </p>
        </div>
      </section>

      <section className="container-x py-10 sticky top-20 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`category-${c.id}`}
              className={`text-xs uppercase tracking-[0.2em] font-bold px-5 py-3 border rounded-full transition-all ${
                active === c.id
                  ? "border-[#2A8BFF] bg-[#2A8BFF] text-white shadow-[0_6px_20px_rgba(42,139,255,0.25)]"
                  : "border-slate-200 text-slate-700 hover:border-slate-400 bg-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      <section className="container-x py-16">
        {isComingSoon ? (
          <div
            data-testid="coming-soon"
            className="text-center max-w-xl mx-auto py-16 px-8 bg-[#F6F8FB] border border-slate-200 rounded-3xl"
          >
            <span className="w-14 h-14 rounded-2xl bg-[#EAF1FE] text-[#1E6FE5] inline-flex items-center justify-center">
              <Sparkles size={26} />
            </span>
            <h3 className="font-outfit text-3xl font-bold mt-6 text-slate-900">
              {CATEGORIES.find((c) => c.id === active)?.label} — coming soon
            </h3>
            <p className="text-slate-600 mt-3">
              Our {active === "kids" ? "kids’ e-cycles" : "electric cargo bikes"} are in the works and arriving to eBikeGo stores shortly. Register your interest and we'll notify you first.
            </p>
            <button onClick={() => openModal()} className="btn-primary mt-8" data-testid="coming-soon-cta">
              Notify Me
            </button>
          </div>
        ) : (
          <>
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </motion.div>
            {filtered.length === 0 && (
              <p className="text-center text-slate-500 py-20">No models in this category yet.</p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
